import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { KERF, TRIM } from "../calculation/generateTables/cutUtils";
import { sizeListKantRows } from "./sizeListKantTotals";

const parseSize = (raw) => {
  try {
    const arr = JSON.parse(raw || "[0,0,18]");
    return {
      height: Number(arr[0] ?? 0),
      width: Number(arr[1] ?? 0),
      thickness: Number(arr[2] ?? 18)
    };
  } catch {
    return { height: 0, width: 0, thickness: 18 };
  }
};

const parseKant = (raw) => {
  if (!raw) {
    return {
      folia1: "0-0",
      folia1Type: "",
      folia2: "0-0",
      folia2Type: ""
    };
  }

  let s = raw.trim();
  if (s.startsWith("[") && s.endsWith("]")) {
    s = s.slice(1, -1);
  }

  const [type, longCountRaw, shortCountRaw] = s.split(",");
  const longCount = Number(longCountRaw ?? 0);
  const shortCount = Number(shortCountRaw ?? 0);

  if (type?.trim() === "04") {
    return {
      folia1: `${longCount}-${shortCount}`,
      folia1Type: "S",
      folia2: "0-0",
      folia2Type: ""
    };
  }

  if (["2", "42", "1"].includes(type?.trim())) {
    return {
      folia1: "0-0",
      folia1Type: "",
      folia2: `${longCount}-${shortCount}`,
      folia2Type: { "2": "G", "42": "LAT", "1": "1L" }[type.trim()]
    };
  }

  return {
    folia1: "0-0",
    folia1Type: "",
    folia2: "0-0",
    folia2Type: ""
  };
};

const parsePositions = (raw) => {
  if (!raw) return [];

  return [...String(raw).matchAll(/\[([^\]]+)\]/g)].map((match) =>
    match[1].split(",").map((v) => Number(v.trim()))
  );
};

const getItemTableIds = (item) => {
  const positions = parsePositions(item?.tablePosition);

  return [
    ...new Set(
      positions
        .map((pos) => pos[3])
        .filter((id) => id != null && !Number.isNaN(id))
    )
  ];
};

const getMillingName = (millingId, millings) => {
  if (!millingId) return "";

  const milling = millings.find(
    (m) => String(m.millingId) === String(millingId)
  );

  return milling?.name || "";
};

const getMillingNames = (item, createdTables, millings) => {
  const tableIds = getItemTableIds(item);
  const millingNames = [
    ...new Set(
      tableIds
        .map((tableId) => createdTables.find((table) => table.id === tableId))
        .map((table) => table?.milling?.millingId)
        .map((millingId) => getMillingName(millingId, millings))
        .filter(Boolean)
    )
  ];

  return millingNames.join(", ");
};

const getColorNameWithMilling = (color, millingName) => {
  const colorName = color?.name ?? "";
  if (!millingName) return colorName;

  const hyphenIndex = colorName.indexOf("-");
  const insertionIndex = hyphenIndex >= 0 ? hyphenIndex : colorName.indexOf(" ");
  if (insertionIndex < 0) return [colorName, millingName].filter(Boolean).join(" ");

  return [
    colorName.slice(0, insertionIndex).trimEnd(),
    millingName,
    colorName.slice(insertionIndex).trimStart()
  ].filter(Boolean).join(" ");
};
const parseDatiItemIds = (dati) => {
  if (!dati) return [];

  return String(dati)
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => /^\d+=/.test(line))
    .map((line) => {
      const value = line.replace(/^\d+=/, "");
      const parts = value.split(",");
      const itemId = Number(parts[12]);

      return Number.isNaN(itemId) ? null : itemId;
    })
    .filter(Boolean);
};

const buildRowsFromCreatedTablesOrder = ({ items, createdTables }) => {
  const itemMap = new Map(items.map((item) => [Number(item.itemId), item]));
  const rows = [];

  createdTables
    .slice()
    .sort((a, b) => Number(a.id) - Number(b.id))
    .forEach((table) => {
      const itemIdsInOrder = parseDatiItemIds(table.dati);

      const qtyByItemId = new Map();

      itemIdsInOrder.forEach((itemId) => {
        qtyByItemId.set(itemId, (qtyByItemId.get(itemId) ?? 0) + 1);
      });

      if (![...qtyByItemId.keys()].some((id) => itemMap.has(id))) return;
      rows.push([{
        content: `${table.id}. tábla${table.split === true ? " - fél tábla" : ""}`,
        colSpan: 9,
        styles: { fillColor: [195, 205, 215], textColor: [25, 35, 45], fontStyle: "bold" }
      }]);

      Array.from(qtyByItemId.entries()).forEach(([itemId, qtyOnTable]) => {
        const item = itemMap.get(Number(itemId));
        if (!item) return;

        const size = parseSize(item.size);
        const kant = parseKant(item.kant);

        const length = size.height;
        const width = size.width;

        rows.push([
          table.id,
          item.description ?? item.details ?? "",
          length.toFixed(1).replace(".", ","),
          width.toFixed(1).replace(".", ","),
          qtyOnTable,
          kant.folia1 === "0-0" ? "" : kant.folia1,
          kant.folia1Type,
          kant.folia2 === "0-0" ? "" : kant.folia2,
          kant.folia2Type
        ]);
      });
    });

  return rows;
};
export function exportSizeListPdf({
  work,
  color,
  items,
  currentTable,
  createdTables = [],
  millings = []
}) {
  const doc = new jsPDF("portrait", "mm", "a4");

  const firstSize = parseSize(items?.[0]?.size);
  const now = new Date();
  const millingName = getMillingNames(
    items?.[0],
    createdTables,
    millings
  );
  const colorNameWithMilling = getColorNameWithMilling(color, millingName);
  const customerName = work?.user?.name ?? work?.username ?? items?.[0]?.user?.name ?? "";
  doc.setFontSize(18);
  let titleX = 12;
  let titleY = 18;
  const titleParts = [
    { text: "Méretlista", style: "normal" },
    ...(customerName ? [{ text: " - ", style: "normal" }, { text: customerName, style: "bold" }] : []),
    ...(work?.client?.name ? [{ text: ` - ${work.client.name}`, style: "normal" }] : [])
  ];
  titleParts.forEach(({ text, style }) => {
    doc.setFont("helvetica", style);
    String(text).split(/(\s+)/).filter(Boolean).forEach((word) => {
      const width = doc.getTextWidth(word);
      if (titleX + width > 198) {
        titleX = 12;
        titleY += 7;
        if (!word.trim()) return;
      }
      doc.text(word, titleX, titleY);
      titleX += width;
    });
  });
  doc.setFont("helvetica", "normal");
  const headerOffset = titleY - 18;
  const itemIds = new Set(items.map((item) => Number(item.itemId)));
  const relevantTables = createdTables.filter((table) =>
    parseDatiItemIds(table.dati).some((id) => itemIds.has(id))
  );
  const tableCount = relevantTables.reduce((sum, table) => sum + (table.split === true ? 0.5 : 1), 0);
  const formattedTableCount = String(tableCount).replace(".", ",");
  const trimTable = currentTable ?? relevantTables[0];
  const rawTrim = Number(trimTable?.trim ?? trimTable?.TRIM ?? TRIM);
  const trim = Number.isFinite(rawTrim) && rawTrim >= 0 ? rawTrim : TRIM;

  const time = now.toLocaleTimeString("hu-HU", {
    hour: "2-digit",
    minute: "2-digit"
  });

  doc.setFontSize(11);

  const headerField = (label, value, x, y, rightEdge) => {
    doc.setFont("helvetica", "bold");
    doc.text(label, x, y + headerOffset);
    const valueX = x + doc.getTextWidth(label) + 3;
    doc.setFont("helvetica", "normal");
    const lines = doc.splitTextToSize(value, rightEdge - valueX);
    doc.text(lines, valueX, y + headerOffset);
    return Math.max(1, lines.length) * 5;
  };
  const workLabel = [work?.workId, work?.name]
    .filter((value) => value != null && value !== "").join(" - ");
  const headerRows = [
    [["Megnevezés:", String(colorNameWithMilling)], ["Munka:", workLabel]],
    [["Táblák száma:", formattedTableCount], ["Vastagság:", `${firstSize.thickness} mm`]],
    [["Vágási szélesség:", `${KERF.toFixed(1)} mm`], ["Szélezés:", `${trim.toFixed(1)} mm`]]
  ];
  let headerY = 34;
  headerRows.forEach(([left, right]) => {
    const leftHeight = headerField(...left, 12, headerY, 102);
    const rightHeight = headerField(...right, 110, headerY, 198);
    headerY += Math.max(10, leftHeight + 5, rightHeight + 5);
  });

  const rows = buildRowsFromCreatedTablesOrder({
    items,
    createdTables: relevantTables
  });

  autoTable(doc, {
    startY: headerY - 2 + headerOffset,
    margin: { top: 18, bottom: 18, left: 12, right: 12 },
    rowPageBreak: "avoid",
    alternateRowStyles: { fillColor: [230, 230, 230] },
    head: [
      [
        "Tábla",
        "Megjegyzés",
        "Hossz.",
        "Szél.",
        "DB",
        "Fólia1",
        "Fólia1 t",
        "Fólia2",
        "Fólia2 t"
      ]
    ],
    body: rows,
    theme: "striped",
    tableWidth: "auto",
    styles: {
      fontSize: 8,
      cellPadding: 1.5,
      overflow: "linebreak"
    },
    headStyles: {
      fontStyle: "bold"
    },
    didParseCell: (data) => {
      if (
        data.section === "body" &&
        (data.column.index === 2 || data.column.index === 3)
      ) {
        data.cell.styles.fontStyle = "bold";
      }
    }
  });

  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 8,
    margin: { top: 18, bottom: 18, left: 12, right: 12 },
    tableWidth: 75,
    pageBreak: "avoid",
    head: [["Kant típusa", "Mennyiség (m)"]],
    body: sizeListKantRows(relevantTables),
    theme: "striped",
    styles: { fontSize: 9, cellPadding: 2 },
    alternateRowStyles: { fillColor: [230, 230, 230] },
    columnStyles: { 1: { halign: "right" } }
  });

  const sanitizeFileName = (value) =>
    String(value ?? "")
      .trim()
      .replace(/[\\/:*?"<>|]/g, "-")
      .replace(/\s+/g, "_");

  const pageCount = doc.getNumberOfPages();
  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    doc.setFontSize(9);
    const pageLabel = `${page} / ${pageCount}`;
    if (page === 1) {
      doc.text(`${now.toLocaleDateString("hu-HU")} ${time}`, 198 - doc.getTextWidth(pageLabel) - 10, 8, { align: "right" });
    }
    doc.text(pageLabel, 198, 8, { align: "right" });
    doc.text(pageLabel, 198, 290, { align: "right" });
  }

  const tablePart = `${formattedTableCount}T`;
  const fileName = [
    work?.user?.name ?? work?.username ?? "user",
    color?.name ?? color?.colorId ?? "color",
    millingName,
    tablePart
  ]
    .map(sanitizeFileName)
    .filter(Boolean)
    .join("_");

  doc.save(`${fileName}.pdf`);
}
